import { Component, DestroyRef, Inject, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import moment from 'moment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminService, AuditLogEntry, AuditLogResult, Employee } from '../../../core/services/admin.service';
import { AdminFeedbackService } from '../../../core/services/admin-feedback.service';

type AuditEntityType =
  | ''
  | 'timerecord'
  | 'statement'
  | 'project'
  | 'activity'
  | 'employee'
  | 'salary'
  | 'team'
  | 'customer'
  | 'erp-invoice'
  | 'absence-record'
  | 'salary-item'
  | 'expense-item'
  | 'internal-time-lock';

@Component({
  selector: 'app-admin-audit-tab',
  standalone: false,
  template: require('./admin-audit-tab.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./admin-audit-tab.component.scss').default],
})
export class AdminAuditTabComponent implements OnChanges {
  @Input() employees: Employee[] = [];

  readonly entityOptions: Array<{ value: AuditEntityType; label: string }> = [
    { value: '', label: 'Alla objekt' },
    { value: 'timerecord', label: 'Tid' },
    { value: 'statement', label: 'Statement' },
    { value: 'project', label: 'Projekt' },
    { value: 'activity', label: 'Aktivitet' },
    { value: 'employee', label: 'Anställd' },
    { value: 'salary', label: 'Lön' },
    { value: 'team', label: 'Team' },
    { value: 'customer', label: 'Kund' },
    { value: 'erp-invoice', label: 'ERP-faktura' },
    { value: 'absence-record', label: 'Frånvaro' },
    { value: 'salary-item', label: 'Lönepost' },
    { value: 'expense-item', label: 'Utläggspost' },
    { value: 'internal-time-lock', label: 'Tidlåsning' },
  ];

  readonly actionOptions: Array<{ value: string; label: string }> = [
    { value: '', label: 'Alla händelser' },
    { value: 'create', label: 'Skapa' },
    { value: 'update', label: 'Ändra' },
    { value: 'delete', label: 'Ta bort' },
    { value: 'set-note', label: 'Sätt anteckning' },
    { value: 'clear-note', label: 'Ta bort anteckning' },
    { value: 'new-price', label: 'Nytt pris' },
    { value: 'bulk-create', label: 'Batch skapa' },
    { value: 'mass-update', label: 'Massuppdatera' },
    { value: 'set-budget', label: 'Sätt budget' },
    { value: 'clear-budget', label: 'Ta bort budget' },
    { value: 'create-statement', label: 'Skapa statement' },
    { value: 'update-statement', label: 'Ändra statement' },
    { value: 'delete-statement', label: 'Ta bort statement' },
    { value: 'send-statement', label: 'Skicka statement' },
    { value: 'create-erp-invoice', label: 'Skapa ERP-faktura' },
    { value: 'update-erp-invoice', label: 'Ändra ERP-faktura' },
    { value: 'lock-internal-time', label: 'Lås intern tid' },
  ];

  fromDateMoment = moment().subtract(14, 'days');
  toDateMoment = moment();
  actorOptions: Employee[] = [];
  selectedActor: Employee | null = null;
  selectedEntityType: AuditEntityType = '';
  selectedAction = '';
  entityIdFilter = '';
  statusOnly = false;

  result: AuditLogResult = {
    total: 0,
    limit: 50,
    offset: 0,
    rows: [],
  };
  selectedEntry: AuditLogEntry | null = null;
  loading = false;

  private destroyRef = inject(DestroyRef);
  private hasLoadedOnce = false;

  constructor(
    @Inject(AdminService) private adminService: AdminService,
    @Inject(AdminFeedbackService) private adminFeedback: AdminFeedbackService
  ) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.actorOptions = [...this.employees].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    if (!this.hasLoadedOnce) {
      this.search(true);
      this.hasLoadedOnce = true;
    }
  }

  get hasPreviousPage(): boolean {
    return this.result.offset > 0;
  }

  get hasNextPage(): boolean {
    return this.result.offset + this.result.limit < this.result.total;
  }

  onFromDateChange(value: moment.Moment | null): void {
    this.fromDateMoment = value;
  }

  onToDateChange(value: moment.Moment | null): void {
    this.toDateMoment = value;
  }

  search(resetOffset = false): void {
    const nextOffset = resetOffset ? 0 : this.result.offset;
    this.loading = true;
    this.adminService.getAuditEntries({
      from: this.fromDateMoment ? this.fromDateMoment.format('YYYY-MM-DD') : null,
      to: this.toDateMoment ? this.toDateMoment.format('YYYY-MM-DD') : null,
      employee_id: this.selectedActor?.employee_id ?? null,
      entity_type: this.selectedEntityType || null,
      action: this.selectedAction || null,
      entity_id: this.entityIdFilter.trim() || null,
      status_only: this.statusOnly,
      limit: this.result.limit,
      offset: nextOffset,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        this.result = result;
        this.selectedEntry = result.rows[0] ?? null;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.adminFeedback.showCrudError('hämta', 'auditlogg', err);
      },
    });
  }

  previousPage(): void {
    if (!this.hasPreviousPage) {
      return;
    }
    this.result = {
      ...this.result,
      offset: Math.max(0, this.result.offset - this.result.limit),
    };
    this.search(false);
  }

  nextPage(): void {
    if (!this.hasNextPage) {
      return;
    }
    this.result = {
      ...this.result,
      offset: this.result.offset + this.result.limit,
    };
    this.search(false);
  }

  selectEntry(entry: AuditLogEntry): void {
    this.selectedEntry = entry;
  }

  getStatusTransition(entry: AuditLogEntry): string {
    return entry.context_json?.transition || '';
  }

  getSummary(entry: AuditLogEntry): string {
    const actor = entry.loginname || 'Okänd användare';
    const entityLabel = this.getEntityLabel(entry.entity_type);
    const entityId = entry.entity_id ? ` ${entry.entity_id}` : '';
    const actionLabel = this.getActionLabel(entry.action).toLowerCase();

    if (entry.action === 'set-note') {
      return `${actor} satte anteckning på ${entityLabel}${entityId}`;
    }
    if (entry.action === 'clear-note') {
      return `${actor} tog bort anteckning på ${entityLabel}${entityId}`;
    }
    if (entry.action === 'new-price') {
      return `${actor} satte nytt pris på ${entityLabel}${entityId}`;
    }
    if (entry.action === 'lock-internal-time') {
      return `${actor} låste intern tid`;
    }
    return `${actor} ${actionLabel} ${entityLabel}${entityId}`.trim();
  }

  getEntityLabel(entityType: string): string {
    return this.entityOptions.find((option) => option.value === entityType)?.label || entityType;
  }

  getActionLabel(action: string): string {
    return this.actionOptions.find((option) => option.value === action)?.label || action;
  }

  getEntries(value: unknown): Array<[string, unknown]> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return [];
    }
    return Object.entries(value as Record<string, unknown>);
  }
}
